<template>
    <div class="app-dock-container">
        <div class="app-dock">
            <a 
                v-for="(item, index) in dockItems" 
                :key="item.name" 
                :href="item.url || '#'" 
                target="_blank" 
                :title="item.name"
                class="dock-item"
            >
                <div 
                    class="icon-wrapper" 
                    :style="{ '--hover-color': item.color }"
                >
                    <div 
                        class="icon-svg-wrapper" 
                        :class="{ 'use-original-colors': item.keepOriginalColors }"
                        v-html="item.svg" 
                        :style="{ color: item.keepOriginalColors ? undefined : item.color }"
                    ></div>
                </div>
                <span class="tooltip">{{ item.name }}</span>
            </a>
        </div>
        <div class="dock-copyright">
            &copy; 2026 Sssuugar (NetRes.cn) | 不涉及任何侵权内容，不做任何商用
        </div>
    </div>
</template>

<script setup>
import { 
    SVG_BILIBILI, SVG_DOUYIN, SVG_ZHICHU, SVG_TIEBA, 
    SVG_DOUBAO, SVG_YUANBAO, SVG_DEEPSEEK, 
    SVG_CHATGPT, SVG_GEMINI, SVG_GROK, SVG_CLAUDE 
} from '@/js/svg-icons.js'; 

// --- Data ---
const dockItems = [
    { name: 'B站', url: 'https://www.bilibili.com', svg: SVG_BILIBILI, color: '#00a1d6' },
    { name: '抖音', url: 'https://www.douyin.com', svg: SVG_DOUYIN, color: '#FE2C55' },
    { name: '知乎', url: 'https://www.zhihu.com', svg: SVG_ZHICHU, color: '#0084FF' },
    { name: '贴吧', url: 'https://tieba.baidu.com', svg: SVG_TIEBA, color: '#0084FF' },
    { name: 'Gemini', url: 'https://gemini.google.com', svg: SVG_GEMINI, color: '#4E89FF' },
    { name: 'ChatGPT', url: 'https://chat.openai.com', svg: SVG_CHATGPT, color: '#10a37f' },
    { name: 'Claude', url: 'https://claude.ai', svg: SVG_CLAUDE, color: '#D97757' },
    { name: 'Grok', url: 'https://grok.com/', svg: SVG_GROK, color: '#ffffff' },
    { name: '豆包', url: 'https://www.doubao.com', svg: SVG_DOUBAO, color: '#4a90e2', keepOriginalColors: true },
    { name: '元宝', url: 'https://yuanbao.tencent.com', svg: SVG_YUANBAO, color: '#00CC71' },
    { name: 'Deepseek', url: 'https://www.deepseek.com', svg: SVG_DEEPSEEK, color: '#4D6BFE' },
];
</script>

<style scoped>
/* Container */
.app-dock-container {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.dock-copyright {
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.8);
    pointer-events: none;
    white-space: nowrap;
}

.app-dock {
    display: flex;
    align-items: center;
    gap: 10px; /* Reduced gap */
    background: rgba(20, 20, 20, 0.7);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 8px 12px; /* Reduced padding */
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
}

/* Dock Item */
.dock-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    text-decoration: none;
    transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.dock-item:hover {
    transform: translateY(-5px);
}

.icon-wrapper {
    width: 40px; /* Reduced from 48px */
    height: 40px;
    border-radius: 10px; /* Slightly tighter radius */
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.dock-item:hover .icon-wrapper {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 15px var(--hover-color, rgba(255, 255, 255, 0.2));
}

.icon-svg-wrapper {
    width: 24px; /* Reduced from 32px */
    height: 24px;
    transition: color 0.2s;
}

.dock-item:hover .icon-svg-wrapper {
    color: #fff;
}

.icon-svg-wrapper > :deep(svg) {
    width: 100%;
    height: 100%;
}

.icon-svg-wrapper:not(.use-original-colors) > :deep(svg path) {
    fill: currentColor !important;
}

/* Tooltip */
.tooltip {
    position: absolute;
    top: -30px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s, top 0.2s;
    white-space: nowrap;
}

.dock-item:hover .tooltip {
    opacity: 1;
    top: -40px;
}

/* Responsive Design */
@media (max-width: 768px) {
    .app-dock-container {
        width: 100%;
        bottom: 20px;
        padding: 0 16px; /* Safe area */
    }

    .app-dock {
        max-width: 100%;
        overflow-x: auto;
        justify-content: flex-start;
        padding: 10px 16px;
        background: rgba(20, 20, 20, 0.85); /* Slightly darker/more opaque */
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        /* Hide scrollbar for cleaner look */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none;  /* IE 10+ */
    }
    
    .app-dock::-webkit-scrollbar { 
        display: none; 
    }

    .dock-item {
        flex-shrink: 0;
        margin-right: 4px; /* Slight spacing */
    }

    .dock-item:last-child {
        margin-right: 0;
    }

    .icon-wrapper {
        width: 44px; /* Slightly larger than previous mobile */
        height: 44px;
        background: rgba(255, 255, 255, 0.08); /* More visible background */
    }

    .icon-svg-wrapper {
        width: 26px;
        height: 26px;
    }

    /* Hide tooltips on touch devices to prevent getting stuck */
    .tooltip {
        display: none;
    }
    
    .dock-copyright {
        font-size: 10px; /* Slightly bigger */
        margin-top: 8px;
        opacity: 0.6;
    }
}
</style>
